/**
 * TransmissionOptionsList
 *
 * Minimalist transmission options display.
 * Contact options as compact cards, manual entry as inline buttons.
 */

import { Loader2Icon } from 'lucide-react';
import React, { useMemo } from 'react';

import type { CorrespondenceChannel } from '@/services/api/correspondenceService';

import type { ContactChannel, RecipientContact } from '../../types';
import { isOptionSelected, type TransmissionOption } from '../types';
import { AddContactHandleButton } from './AddContactHandleButton';
import { ContactOptionCard } from './ContactOptionCard';
import { ManualEntryButtons } from './ManualEntryButtons';

interface TransmissionOptionsListProps {
  contacts: RecipientContact[];
  selectedOption: TransmissionOption | null;
  onSelectOption: (option: TransmissionOption) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  /** Recipient info for "Add contact" when recipient has no contacts yet */
  recipientId?: string | null;
  recipientEntityType?: string | null;
  recipientName?: string | null;
}

// Build contact options from contacts
function buildContactOptions(
  contacts: RecipientContact[],
): Array<{ contact: RecipientContact; channel: ContactChannel }> {
  const options: Array<{ contact: RecipientContact; channel: ContactChannel }> =
    [];

  contacts.forEach((contact) => {
    contact.supportedChannels.forEach((channel) => {
      options.push({ contact, channel });
    });
  });

  return options;
}

export const TransmissionOptionsList: React.FC<
  TransmissionOptionsListProps
> = ({
  contacts,
  selectedOption,
  onSelectOption,
  isDisabled,
  isLoading,
  recipientId,
  recipientEntityType,
  recipientName,
}) => {
  // Build contact options
  const contactOptions = useMemo(
    () => buildContactOptions(contacts),
    [contacts],
  );

  // Derive manual entry state
  const isManualEntry = selectedOption?.type === 'manual';
  const selectedChannel = selectedOption?.channel ?? null;

  // Handle contact selection
  const handleSelectContact = (
    contact: RecipientContact,
    channel: ContactChannel,
  ) => {
    onSelectOption({ type: 'contact', contact, channel });
  };

  // Handle manual entry selection
  const handleSelectManual = (channel: CorrespondenceChannel) => {
    onSelectOption({ type: 'manual', channel });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2Icon className="h-4 w-4 animate-spin text-zinc-400" />
        <span className="ml-2 text-[13px] text-zinc-400">Loading...</span>
      </div>
    );
  }

  const hasContacts = contactOptions.length > 0;

  // Determine carrier info for "Add contact" button
  // Prefer first contact's source, fall back to recipient props
  const isInsuranceCarrier =
    contactOptions[0]?.contact.source.entityType === 'insurance_carrier' ||
    recipientEntityType === 'insurance_carrier';
  const carrierId =
    contactOptions[0]?.contact.source.entityId ?? recipientId ?? null;
  const carrierName =
    contactOptions[0]?.contact.source.entityName ?? recipientName ?? null;

  // Show "Add contact" for insurance carriers
  const showAddContact = isInsuranceCarrier && carrierId && carrierName;

  // Render contacts section
  const renderContactsSection = () => {
    if (hasContacts) {
      return (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="text-[11px] font-medium text-zinc-400 uppercase tracking-wide">
              Contacts
            </h4>
            {showAddContact && (
              <AddContactHandleButton
                carrierId={carrierId}
                carrierName={carrierName}
              />
            )}
          </div>
          <div className="space-y-0.5">
            {contactOptions.map(({ contact, channel }) => {
              const option: TransmissionOption = {
                type: 'contact',
                contact,
                channel,
              };
              return (
                <ContactOptionCard
                  key={`${contact.id}-${channel}`}
                  contact={contact}
                  channel={channel}
                  isSelected={isOptionSelected(selectedOption, option)}
                  onSelect={() => handleSelectContact(contact, channel)}
                  isDisabled={isDisabled}
                />
              );
            })}
          </div>
        </div>
      );
    }

    if (showAddContact) {
      // No contacts yet, but show "Add contact" for insurance carriers
      return (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="text-[11px] font-medium text-zinc-400 uppercase tracking-wide">
              Contacts
            </h4>
            <AddContactHandleButton
              carrierId={carrierId}
              carrierName={carrierName}
            />
          </div>
          <p className="text-[12px] text-zinc-400 py-3">
            No contacts on file for this carrier.
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Contact options */}
      {renderContactsSection()}

      {/* Manual entry section */}
      <div>
        <h4 className="text-[11px] font-medium text-zinc-400 uppercase tracking-wide mb-1.5">
          {hasContacts ? 'Or enter manually' : 'Enter manually'}
        </h4>
        <ManualEntryButtons
          selectedChannel={selectedChannel}
          isManualEntry={isManualEntry}
          onSelect={handleSelectManual}
          isDisabled={isDisabled}
        />
      </div>
    </div>
  );
};
